import { enCommon } from './en/common.js';
import { enEmail } from './en/email.js';
import { enHost } from './en/host.js';
import { enOperator } from './en/operator.js';
import { enShared } from './en/shared.js';
import { enWidget } from './en/widget.js';
import { deCommon } from './de/common.js';
import { deEmail } from './de/email.js';
import { deHost } from './de/host.js';
import { deOperator } from './de/operator.js';
import { deShared } from './de/shared.js';
import { deWidget } from './de/widget.js';

/**
 * Translations, shared by the server (emails) and the browser (guest page and
 * dashboard). English is the source of truth: each German file is typed against
 * its English file, so a missing key is a compile error, and a test checks the
 * placeholders match.
 */
export const LOCALES = ['en', 'de'] as const;
export type Locale = (typeof LOCALES)[number];
export const DEFAULT_LOCALE: Locale = 'en';

export const LOCALE_NAMES: Record<Locale, string> = { en: 'English', de: 'Deutsch' };
/** BCP 47 tags used for number, date and time formatting. */
export const LOCALE_TAGS: Record<Locale, string> = { en: 'en-US', de: 'de-DE' };

const en = { ...enCommon, ...enEmail, ...enShared, ...enWidget, ...enHost, ...enOperator };
const de: Record<keyof typeof en, string> = { ...deCommon, ...deEmail, ...deShared, ...deWidget, ...deHost, ...deOperator };
export const DICTIONARIES: Record<Locale, Record<string, string>> = { en, de };
export type MessageKey = keyof typeof en;

export const isLocale = (v: unknown): v is Locale => typeof v === 'string' && (LOCALES as readonly string[]).includes(v);

/** The first candidate that is a supported locale, matching "de-AT" to "de". */
export function pickLocale(candidates: (string | null | undefined)[], fallback: Locale = DEFAULT_LOCALE): Locale {
  for (const c of candidates) {
    if (!c) continue;
    const base = c.toLowerCase().split(/[-_]/)[0];
    if (isLocale(base)) return base;
  }
  return fallback;
}

export type Vars = Record<string, string | number>;

export function fill(template: string, vars: Vars): string {
  return template.replace(/\{(\w+)\}/g, (m, k) => (k in vars ? String(vars[k]) : m));
}

/** Look up a message and fill {placeholders}. Falls back to English, then to the key. */
export function t(locale: Locale, key: MessageKey | (string & {}), vars: Vars = {}): string {
  const raw = DICTIONARIES[locale]?.[key] ?? DICTIONARIES.en[key] ?? key;
  return fill(raw, vars);
}

/** Plural by count: uses `${key}_one` for 1 and `${key}_other` otherwise (same rule in en and de). */
export function tn(locale: Locale, key: string, n: number, vars: Vars = {}): string {
  return t(locale, `${key}_${n === 1 ? 'one' : 'other'}`, { n, ...vars });
}

export function formatDate(d: Date | string, tz: string, locale: Locale, opts: Intl.DateTimeFormatOptions = {}): string {
  return new Intl.DateTimeFormat(LOCALE_TAGS[locale], { timeZone: tz, ...opts }).format(
    typeof d === 'string' ? new Date(d) : d,
  );
}

/** "Thursday, October 22 at 7:00 PM" / "Donnerstag, 22. Oktober um 19:00 Uhr". */
export function formatWhen(d: Date | string, tz: string, locale: Locale): string {
  const date = formatDate(d, tz, locale, { weekday: 'long', month: 'long', day: 'numeric' });
  const time = formatDate(d, tz, locale, { hour: locale === 'de' ? '2-digit' : 'numeric', minute: '2-digit' });
  return t(locale, 'when.format', { date, time });
}

export function formatMoney(minor: number, currency: string, locale: Locale): string {
  return new Intl.NumberFormat(LOCALE_TAGS[locale], { style: 'currency', currency }).format(minor / 100);
}
